import React, { useState, useEffect } from 'react';
import Header from '../components/Header';
import { useNavigate } from 'react-router-dom';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { 
  ChevronLeftIcon, 
  FunnelIcon, 
  XMarkIcon, 
  ChevronDownIcon, 
  CurrencyRupeeIcon,
  MagnifyingGlassIcon 
} from '@heroicons/react/24/outline';
import { Menu } from '@headlessui/react';
import { motion, AnimatePresence } from 'framer-motion';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import sessionCheck from "../session/userData";
import { encryptDataHybrid } from "../enc/useEncrypt";
import { getDeviceInfo } from '../utils/deviceInfo';

const API_URL = process.env.REACT_APP_API_URL;

const ReportProviders = () => {
    const [salesData, setSalesData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [filterDrawerOpen, setFilterDrawerOpen] = useState(false);
    const [access_data, setAccess_data] = useState([]);
    const navigate = useNavigate();

    // Provider states
    const [providers, setProviders] = useState([]);
    const [providerSearch, setProviderSearch] = useState('');

    const [filters, setFilters] = useState({
        dateRange: 'Today',
        startDate: new Date(),
        endDate: new Date(),
        provider: '', // Added provider filter
    });

    const dateRangeOptions = ['Today', 'This Week', 'This Month', 'Last Month', 'Custom Range'];

    // Filter providers based on search
    const filteredProviders = providers.filter(provider =>
        provider.name.toLowerCase().includes(providerSearch.toLowerCase())
    );

    // Check session
    useEffect(() => {
        if (sessionCheck() == null) {
            navigate("../login", { replace: true });
        } else {
            setAccess_data(JSON.parse(sessionCheck()));
        }
    }, [navigate]);

    useEffect(() => {
        if (access_data?.username) {
            fetchProviders();
            fetchSalesReport();
        }
    }, [access_data?.username]);

    useEffect(() => {
        if (filterDrawerOpen) {
            document.body.style.overflow = "hidden";
        } else {
            document.body.style.overflow = "auto";
        }

        return () => {
            document.body.style.overflow = "auto";
        };
    }, [filterDrawerOpen]);

    // Calculate summary data
    const calculateSummary = () => {
        if (!salesData.length) return null;

        const summary = salesData.reduce((acc, sale) => {
            acc.totalSaleAmount += parseFloat(sale.sales) || 0;
            acc.totalUserProfit += parseFloat(sale.user_profit) || 0;
            acc.totalDistributorProfit += parseFloat(sale.distributer_profit) || 0;
            acc.totalAdminProfit += parseFloat(sale.admin_profit) || 0;
            return acc;
        }, {
            totalSaleAmount: 0,
            totalUserProfit: 0,
            totalDistributorProfit: 0,
            totalAdminProfit: 0
        });

        return summary;
    };

    const fetchProviders = async () => {
        try {
            const dataToEncrypt = {
                user_id: access_data?.username || '',
                login_id: access_data?.login_id || '',
                device_id: getDeviceInfo().deviceId,
                payload: {
                    query: 'transactional-provider'
                }
            };

            const encryptedMessage = encryptDataHybrid(dataToEncrypt);

            if (!encryptedMessage) {
                toast.error("Encryption failed. Please try again.");
                return;
            }

            const payloadToSend = {
                encrypted_key: encryptedMessage.encryptedKey,
                encrypted_data: encryptedMessage.encryptedData,
            };

            const response = await fetch(`${API_URL}provider-operation.php`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${Math.floor(Date.now() / 1000) * 784116}`,
                },
                body: JSON.stringify(payloadToSend),
            });

            const result = await response.json();

            if (result.status === true) {
                setProviders(result.data || []);
            } else {
                throw new Error(result.message || "Failed to fetch providers");
            }
        } catch (error) {
            toast.error(error.message || "Error fetching providers");
        }
    };

    const fetchSalesReport = async () => {
        setLoading(true);
        try {
            const formatDate = (date) => {
                return date ? new Date(date).toISOString().split('T')[0] : '';
            };

            const dataToEncrypt = {
                user_id: access_data?.username || '',
                login_id: access_data?.login_id || '',
                device_id: getDeviceInfo().deviceId,
                payload: {
                    date_range: filters.dateRange,
                    from_date: formatDate(filters.startDate),
                    to_date: formatDate(filters.endDate),
                    provider_id: filters.provider || '' // Added provider filter
                }
            };

            console.log("Sales Report Request Payload:", dataToEncrypt);

            const encryptedMessage = encryptDataHybrid(dataToEncrypt);

            if (!encryptedMessage) {
                toast.error("Encryption failed. Please try again.");
                setLoading(false);
                return;
            }

            const payloadToSend = {
                encrypted_key: encryptedMessage.encryptedKey,
                encrypted_data: encryptedMessage.encryptedData,
            };

            const response = await fetch(`${API_URL}report-providers.php`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${Math.floor(Date.now() / 1000) * 784116}`,
                },
                body: JSON.stringify(payloadToSend),
            });

            const result = await response.json();

            if (result.status === true) {
                setSalesData(result.data || []);
            } else {
                toast.error(result.message || "Failed to fetch sales data.");
                setSalesData([]);
            }
        } catch (error) {
            toast.error("An unexpected error occurred. Please try again.");
            console.error("API Error:", error);
            setSalesData([]);
        } finally {
            setLoading(false);
        }
    };

    const handleFilterChange = (name, value) => {
        setFilters(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleDateRangeChange = (range) => {
        const today = new Date();
        let startDate, endDate = today;

        switch (range) {
            case 'Today':
                startDate = new Date(today);
                break;
            case 'This Week':
                startDate = new Date(today.setDate(today.getDate() - today.getDay()));
                break;
            case 'This Month':
                startDate = new Date(today.getFullYear(), today.getMonth(), 1);
                break;
            case 'Last Month':
                startDate = new Date(today.getFullYear(), today.getMonth() - 1, 1);
                endDate = new Date(today.getFullYear(), today.getMonth(), 0);
                break;
            default:
                startDate = filters.startDate;
                endDate = filters.endDate;
        }

        setFilters(prev => ({
            ...prev,
            dateRange: range,
            startDate,
            endDate
        }));
    };

    const applyFilters = () => {
        fetchSalesReport();
        setFilterDrawerOpen(false);
    };

    const resetFilters = () => {
        setFilters({
            dateRange: 'Today',
            startDate: new Date(),
            endDate: new Date(),
            provider: '',
        });
        setProviderSearch('');
    };

    const summary = calculateSummary();

    return (
        <div className="min-h-screen bg-gray-50 text-gray-900 dark:bg-gray-900 dark:text-white">
            <div className="flex flex-col flex-1">
                {/* Header Component */}
                <Header />

                <main className="flex-1 p-6 overflow-auto">
                    <div className="max-w-7xl mx-auto">
                        {/* Header */}
                        <div className="flex items-center justify-between mb-6">
                            <button
                                onClick={() => navigate(-1)}
                                className="flex items-center text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-600"
                            >
                                <ChevronLeftIcon className="h-5 w-5 mr-1" />
                                Back
                            </button>
                            <h2 className="text-xl font-semibold">
                                Provider Report
                            </h2>
                            <button
                                onClick={() => setFilterDrawerOpen(true)}
                                className="p-2 rounded-full hover:bg-gray-300 bg-gray-200 dark:hover:bg-gray-700 flex items-center justify-center"
                            >
                                <FunnelIcon className="h-5 w-5 text-gray-600 dark:text-gray-300" />
                            </button>
                        </div>

                        {/* Filter Drawer */}
                        <AnimatePresence>
                            {filterDrawerOpen && (
                                <>
                                    <motion.div
                                        initial={{ x: '-100%' }}
                                        animate={{ x: 0 }}
                                        exit={{ x: '-100%' }}
                                        transition={{ type: 'tween' }}
                                        className="fixed inset-y-0 left-0 z-50 w-80 bg-white dark:bg-gray-800 shadow-lg overflow-y-auto"
                                    >
                                        <div className="p-4">
                                            <div className="flex items-center justify-between mb-4">
                                                <h2 className="text-lg font-medium">Filter Provider Report</h2>
                                                <button
                                                    onClick={() => setFilterDrawerOpen(false)}
                                                    className="p-2 rounded-full hover:bg-gray-200 dark:hover:bg-gray-700"
                                                >
                                                    <XMarkIcon className="h-5 w-5 text-gray-600 dark:text-gray-300" />
                                                </button>
                                            </div>

                                            <div className="space-y-4">
                                                {/* Date Range */}
                                                <div className="space-y-1">
                                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Date Range</label>
                                                    <Menu as="div" className="relative">
                                                        <Menu.Button className="w-full flex justify-between items-center px-3 py-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-left text-sm focus:outline-none">
                                                            <span>{filters.dateRange}</span>
                                                            <ChevronDownIcon className="h-4 w-4" />
                                                        </Menu.Button>
                                                        <Menu.Items className="absolute z-10 mt-1 w-full bg-white dark:bg-gray-700 shadow-lg rounded-md py-1 text-base ring-1 ring-black ring-opacity-5 focus:outline-none">
                                                            {dateRangeOptions.map((option) => (
                                                                <Menu.Item key={option}>
                                                                    {({ active }) => (
                                                                        <button
                                                                            onClick={() => handleDateRangeChange(option)}
                                                                            className={`${active ? 'bg-gray-100 dark:bg-gray-600' : ''} block w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-200`}
                                                                        >
                                                                            {option}
                                                                        </button>
                                                                    )}
                                                                </Menu.Item>
                                                            ))}
                                                        </Menu.Items>
                                                    </Menu>
                                                    {filters.dateRange === 'Custom Range' && (
                                                        <div className="grid grid-cols-2 gap-2 mt-2">
                                                            <div>
                                                                <DatePicker
                                                                    selected={filters.startDate}
                                                                    onChange={(date) => handleFilterChange('startDate', date)}
                                                                    selectsStart
                                                                    startDate={filters.startDate}
                                                                    endDate={filters.endDate}
                                                                    popperPlacement="bottom-start"
                                                                    popperClassName="z-50"
                                                                    popperProps={{
                                                                        modifiers: [
                                                                            {
                                                                                name: "preventOverflow",
                                                                                options: {
                                                                                    boundary: "viewport",
                                                                                },
                                                                            },
                                                                        ],
                                                                    }}
                                                                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm bg-white dark:bg-gray-700"
                                                                />
                                                            </div>
                                                            <div>
                                                                <DatePicker
                                                                    selected={filters.endDate}
                                                                    onChange={(date) => handleFilterChange('endDate', date)}
                                                                    selectsEnd
                                                                    startDate={filters.startDate}
                                                                    endDate={filters.endDate}
                                                                    minDate={filters.startDate}
                                                                    popperPlacement="bottom-end"
                                                                    popperClassName="z-50"
                                                                    popperProps={{
                                                                        modifiers: [
                                                                            {
                                                                                name: "preventOverflow",
                                                                                options: {
                                                                                    boundary: "viewport",
                                                                                },
                                                                            },
                                                                        ],
                                                                    }}
                                                                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm bg-white dark:bg-gray-700"
                                                                />
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>

                                                {/* Provider */}
                                                <div className="space-y-1">
                                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Provider</label>
                                                    <Menu as="div" className="relative">
                                                        <Menu.Button className="w-full flex justify-between items-center px-3 py-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-left text-sm focus:outline-none">
                                                            <span>
                                                                {filters.provider
                                                                    ? providers.find(p => p.id === filters.provider)?.name || 'Select Provider'
                                                                    : 'All Providers'}
                                                            </span>
                                                            <ChevronDownIcon className="h-4 w-4" />
                                                        </Menu.Button>
                                                        <Menu.Items className="absolute z-10 mt-1 w-full bg-white dark:bg-gray-700 shadow-lg rounded-md py-1 text-base ring-1 ring-black ring-opacity-5 focus:outline-none max-h-60 overflow-auto">
                                                            <div className="sticky top-0 bg-white dark:bg-gray-700 px-2 py-1">
                                                                <div className="relative">
                                                                    <input
                                                                        type="text"
                                                                        value={providerSearch}
                                                                        onChange={(e) => setProviderSearch(e.target.value)}
                                                                        className="w-full pl-8 pr-2 py-1 border border-gray-300 dark:border-gray-600 rounded text-sm bg-white dark:bg-gray-700"
                                                                        placeholder="Search provider..."
                                                                    />
                                                                    <MagnifyingGlassIcon className="absolute left-2 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                                                                </div>
                                                            </div>
                                                            <Menu.Item>
                                                                {({ active }) => (
                                                                    <button
                                                                        onClick={() => {
                                                                            handleFilterChange('provider', '');
                                                                            setProviderSearch('');
                                                                        }}
                                                                        className={`${active ? 'bg-gray-100 dark:bg-gray-600' : ''} block w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-200`}
                                                                    >
                                                                        All Providers
                                                                    </button>
                                                                )}
                                                            </Menu.Item>
                                                            {filteredProviders.map((provider) => (
                                                                <Menu.Item key={provider.id}>
                                                                    {({ active }) => (
                                                                        <button
                                                                            onClick={() => {
                                                                                handleFilterChange('provider', provider.id);
                                                                                setProviderSearch('');
                                                                            }}
                                                                            className={`${active ? 'bg-gray-100 dark:bg-gray-600' : ''} block w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-200`}
                                                                        >
                                                                            {provider.name}
                                                                        </button>
                                                                    )}
                                                                </Menu.Item>
                                                            ))}
                                                        </Menu.Items>
                                                    </Menu>
                                                </div>
                                            </div>

                                            <div className="mt-6 flex space-x-3">
                                                <button
                                                    onClick={resetFilters}
                                                    className="flex-1 px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-200 rounded-md hover:bg-gray-300 dark:hover:bg-gray-600"
                                                >
                                                    Reset
                                                </button>
                                                <button
                                                    onClick={applyFilters}
                                                    className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                                                >
                                                    Apply Filters
                                                </button>
                                            </div>
                                        </div>
                                    </motion.div>

                                    {/* Overlay */}
                                    <div
                                        className="fixed inset-0 bg-black bg-opacity-50 z-40"
                                        onClick={() => setFilterDrawerOpen(false)}
                                    />
                                </>
                            )}
                        </AnimatePresence>

                        {/* Summary Cards */}
                        {summary && (
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
                                <div className="bg-blue-500 rounded-lg shadow p-6 text-white">
                                    <div className="flex justify-between items-start">
                                        <div>
                                            <p className="text-sm font-medium opacity-80">Total Sales</p>
                                            <h3 className="text-2xl font-bold mt-1">₹{summary.totalSaleAmount.toFixed(2)}</h3>
                                        </div>
                                        <div className="p-2 rounded-full bg-white bg-opacity-20">
                                            <CurrencyRupeeIcon className="h-6 w-6 text-white" />
                                        </div>
                                    </div>
                                </div>

                                <div className="bg-green-500 rounded-lg shadow p-6 text-white">
                                    <div className="flex justify-between items-start">
                                        <div>
                                            <p className="text-sm font-medium opacity-80">User Profit</p>
                                            <h3 className="text-2xl font-bold mt-1">₹{summary.totalUserProfit.toFixed(2)}</h3>
                                        </div>
                                        <div className="p-2 rounded-full bg-white bg-opacity-20">
                                            <CurrencyRupeeIcon className="h-6 w-6 text-white" />
                                        </div>
                                    </div>
                                </div>

                                <div className="bg-purple-500 rounded-lg shadow p-6 text-white">
                                    <div className="flex justify-between items-start">
                                        <div>
                                            <p className="text-sm font-medium opacity-80">Distributor Profit</p>
                                            <h3 className="text-2xl font-bold mt-1">₹{summary.totalDistributorProfit.toFixed(2)}</h3>
                                        </div>
                                        <div className="p-2 rounded-full bg-white bg-opacity-20">
                                            <CurrencyRupeeIcon className="h-6 w-6 text-white" />
                                        </div>
                                    </div>
                                </div>

                                <div className="bg-orange-500 rounded-lg shadow p-6 text-white">
                                    <div className="flex justify-between items-start">
                                        <div>
                                            <p className="text-sm font-medium opacity-80">Admin Profit</p>
                                            <h3 className="text-2xl font-bold mt-1">₹{summary.totalAdminProfit.toFixed(2)}</h3>
                                        </div>
                                        <div className="p-2 rounded-full bg-white bg-opacity-20">
                                            <CurrencyRupeeIcon className="h-6 w-6 text-white" />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Sales Table */}
                        <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
                            {loading ? (
                                <div className="flex justify-center items-center py-12">
                                    <div className="animate-spin rounded-full h-12 w-12 border-4 border-blue-500 border-t-transparent"></div>
                                </div>
                            ) : salesData.length > 0 ? (
                                <div className="overflow-x-auto">
                                    <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-600">
                                        <thead className="bg-gray-50 dark:bg-gray-700">
                                            <tr>
                                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                                    Category
                                                </th>
                                                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                                    Sale Amount
                                                </th>
                                                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                                    User Profit
                                                </th>
                                                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                                    Distributor Profit
                                                </th>
                                                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                                    Admin Profit
                                                </th>
                                            </tr>
                                        </thead>
                                        <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-600">
                                            {salesData.map((sale, index) => (
                                                <tr key={index} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                                                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white">
                                                        {sale.category}
                                                    </td>
                                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-right text-gray-900 dark:text-white">
                                                        ₹{parseFloat(sale.sales).toFixed(2)}
                                                    </td>
                                                    <td className={`px-6 py-4 whitespace-nowrap text-sm text-right ${parseFloat(sale.user_profit) >= 0 ? 'text-green-600' : 'text-red-600'
                                                        }`}>
                                                        ₹{parseFloat(sale.user_profit).toFixed(2)}
                                                    </td>
                                                    <td className={`px-6 py-4 whitespace-nowrap text-sm text-right ${parseFloat(sale.distributer_profit) >= 0 ? 'text-green-600' : 'text-red-600'
                                                        }`}>
                                                        ₹{parseFloat(sale.distributer_profit).toFixed(2)}
                                                    </td>
                                                    <td className={`px-6 py-4 whitespace-nowrap text-sm text-right ${parseFloat(sale.admin_profit) >= 0 ? 'text-green-600' : 'text-red-600'
                                                        }`}>
                                                        ₹{parseFloat(sale.admin_profit).toFixed(2)}
                                                    </td>
                                                </tr>
                                            ))}

                                            {/* Summary Row */}
                                            {summary && (
                                                <tr className="bg-gray-50 dark:bg-gray-700 font-semibold border-t-2 border-gray-300 dark:border-gray-600">
                                                    <td className="px-6 py-4 whitespace-nowrap text-sm font-bold text-gray-900 dark:text-white">
                                                        TOTAL
                                                    </td>
                                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-right font-bold text-gray-900 dark:text-white">
                                                        ₹{summary.totalSaleAmount.toFixed(2)}
                                                    </td>
                                                    <td className={`px-6 py-4 whitespace-nowrap text-sm text-right font-bold ${summary.totalUserProfit >= 0 ? 'text-green-600' : 'text-red-600'
                                                        }`}>
                                                        ₹{summary.totalUserProfit.toFixed(2)}
                                                    </td>
                                                    <td className={`px-6 py-4 whitespace-nowrap text-sm text-right font-bold ${summary.totalDistributorProfit >= 0 ? 'text-green-600' : 'text-red-600'
                                                        }`}>
                                                        ₹{summary.totalDistributorProfit.toFixed(2)}
                                                    </td>
                                                    <td className={`px-6 py-4 whitespace-nowrap text-sm text-right font-bold ${summary.totalAdminProfit >= 0 ? 'text-green-600' : 'text-red-600'
                                                        }`}>
                                                        ₹{summary.totalAdminProfit.toFixed(2)}
                                                    </td>
                                                </tr>
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                            ) : (
                                <div className="text-center py-12">
                                    <div className="text-gray-500 dark:text-gray-400">
                                        No sales data found for the selected period
                                    </div>
                                    <button
                                        onClick={fetchSalesReport}
                                        className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                                    >
                                        Refresh Data
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                </main>
            </div>
            <ToastContainer />
        </div>
    );
};

export default ReportProviders;